import { useId } from "react";

import type { ConsoleConfig, EvidenceKind } from "../../api/types";
import { offerableKinds } from "../../lib/evidence";
import { Field } from "../ui/Field";

interface EvidenceFieldsProps {
  config: ConsoleConfig | undefined;
  /** The kind chosen so far, or `""` while nothing is chosen. */
  kind: EvidenceKind | "";
  /** What somebody can go and look at, as typed. Empty is a real answer. */
  reference: string;
  onKind: (kind: EvidenceKind) => void;
  onReference: (reference: string) => void;
}

/**
 * What kind of thing the source is, and the pointer to it.
 *
 * The two controls that sit under *"where did the client say so"*, in one place
 * because both screens that ask the question ask it in the same words. The
 * kinds come from `GET /config`: this form offers what the console published as
 * offerable and nothing else, so the kind the console writes for itself —
 * a statement a client signed — is never in a picker, and no fifth kind is
 * invented here on the day somebody adds one to the server (ADR-0013).
 *
 * Args:
 *     config: The published configuration, when it has arrived.
 *     kind: The kind chosen so far.
 *     reference: The pointer typed so far.
 *     onKind: Called with the kind that was picked.
 *     onReference: Called with what was typed.
 *
 * Returns:
 *     The kind cards and the reference field, or the kind cards' honest empty
 *     state while the console has published nothing to offer.
 */
export function EvidenceFields({
  config,
  kind,
  reference,
  onKind,
  onReference,
}: EvidenceFieldsProps): JSX.Element {
  const group = useId();
  const referenceId = useId();
  const offerable = offerableKinds(config);

  return (
    <>
      <fieldset className="form__block">
        <legend className="form__legend">What kind of source is that?</legend>
        {offerable.length > 0 ? (
          <div className="choice-row">
            {offerable.map((entry) => (
              <label
                className="choice"
                key={entry.kind}
                data-selected={kind === entry.kind}
              >
                <input
                  className="sr-only"
                  type="radio"
                  name={group}
                  value={entry.kind}
                  checked={kind === entry.kind}
                  onChange={() => onKind(entry.kind)}
                />
                <span className="choice__name">{entry.label}</span>
              </label>
            ))}
          </div>
        ) : null}
        {offerable.length === 0 ? (
          <p className="form__note">
            The console has not published which kinds of source it takes, so
            there is nothing to pick from yet.
          </p>
        ) : (
          <p className="form__note">
            The kind stays with the permission: a refusal reads it out, so a DPA
            clause and a colleague&rsquo;s note stop being the same sentence. A
            note is a real answer.
          </p>
        )}
      </fieldset>

      <Field
        label="What can somebody go and look at?"
        htmlFor={referenceId}
        hint="Optional: a clause reference, a mail's date and sender, a document id. Leave it empty rather than typing “n/a” — an empty box is an honest absence."
      >
        <input
          id={referenceId}
          className="input"
          value={reference}
          onChange={(event) => onReference(event.target.value)}
          placeholder="amendment 2026-07-30, signed C. Meyer"
        />
      </Field>
    </>
  );
}
